<template>
    <tbody :class="{ 'md-editable-grid-body-append': isAppendSection }">
        <tr
            v-for="(item, rowIndex) in d_rows"
            :key="getRowKey(item, rowIndex)"
            :class="{
                'b-table-row-selected rowSelected': isRowSelected(item, rowIndex)
            }"
            :style="rowStyle"
            @click="onRowClick(rowIndex)"
            @dblclick="onRowDblClick(rowIndex)"
        >
            <!-- Action Column -->
            <editable-action-cell
                v-if="actions.length !== 0 || (showSelectAll && !isAppendSection)"
                :actions="actions"
                :rowIndex="rowIndex"
                :rowKey="getRowKey(item, rowIndex)"
                :item="item"
                :canExecuteAction="canExecuteAction"
                :isAppendSection="isAppendSection"
                :isSelected="isRowSelected(item, rowIndex)"
                :frozen="frozenColumnAction"
                @select-row="onSelectRow"
            >
            </editable-action-cell>

            <!-- Empty Action Column -->
            <editable-empty-action-cell
                v-if="actions.length === 0 && showSelectAll && isAppendSection"
                :frozen="frozenColumnAction"
            >
            </editable-empty-action-cell>

            <!--Component Cells -->
            <component
                v-for="(field, columnIndex) in columns"
                :key="`${rowIndex}_${columnIndex}`"
                :is="getComponentByFieldType(field.dataType)"
                v-bind="field.props"
                :rowIndex="rowIndex"
                :rowKey="getRowKey(item, rowIndex)"
                :columnIndex="columnIndex"
                :columnKey="field.key"
                :value="item[field.key]"
                :item="item"
                @input="updateCellValue($event, item, field)"
                :name="resolveCellName(rowIndex, columnIndex)"
                :ref="resolveCellName(rowIndex, columnIndex)"
                :editable="isEditableField(field)"
                :focusable="isFocusableField(field)"
                :formatter="field.formatter ? formatterHandle(field.formatter, item) : null"
                :isFocused="isFocusedCell(rowIndex, columnIndex)"
                :validatorRules="field.validator"
                :isAppendSection="isAppendSection"
                @nextCell="focusCell(rowIndex, columnIndex + 1)"
                @prevCell="focusCell(rowIndex, columnIndex - 1)"
                @upCell="focusCell(rowIndex - 1, columnIndex)"
                @downCell="focusCell(rowIndex + 1, columnIndex)"
                @cellFocus="setActiveCell"
                @requestEdit="handleRequestEdit"
                @validationState="updateValidation"
            >
                <template v-slot:[`cell-${field.key}`]="slotProps">
                    <slot :name="`cell-${field.key}`" v-bind="slotProps" />
                </template>
            </component>
        </tr>
    </tbody>
</template>

<script>
import EditableInputCell from './EditableCells/EditableInputCell.vue';
import EditableNumericCell from './EditableCells/EditableNumericCell.vue';
import EditableSelectCell from './EditableCells/EditableSelectCell.vue';
import EditableLookupCell from './EditableCells/EditableLookupCell.vue';
import EditableDateCell from './EditableCells/EditableDateCell.vue';
import EditableDateTimeCell from './EditableCells/EditableDateTimeCell.vue';
import EditableTimeCell from './EditableCells/EditableTimeCell.vue';
import EditableButtonCell from './EditableCells/EditableButtonCell.vue';
import ShowMessage from '@/components/messages/ShowMessage';
import Utils from '@/common/utils';
import EditableActionCell from './EditableCells/EditableActionCell.vue';
import EditableEmptyActionCell from './EditableCells/EditableEmptyActionCell.vue';

export default {
    name: 'EditableTableBody',
    mixins: [ShowMessage],
    inject: ['$parentInstance'],
    components: {
        EditableInputCell,
        EditableNumericCell,
        EditableSelectCell,
        EditableLookupCell,
        EditableDateCell,
        EditableDateTimeCell,
        EditableTimeCell,
        EditableActionCell,
        EditableEmptyActionCell,
        EditableButtonCell
    },
    props: {
        columns: {
            type: Array,
            required: true,
            validator: value => {
                return value.every(
                    column =>
                        typeof column.key === 'string' &&
                        typeof column.label === 'string' &&
                        (column.editable === undefined || typeof column.editable === 'boolean') &&
                        (column.class === undefined || typeof column.class === 'string') &&
                        (column.dataType === undefined || typeof column.dataType === 'string') &&
                        (column.formatter === undefined ||
                            typeof column.formatter === 'function') &&
                        (column.props === undefined || typeof column.props === 'object')
                );
            }
        },
        rows: {
            type: Array,
            required: true
        },
        actions: {
            type: Array,
            default: () => []
        },
        rowStyle: {
            type: String,
            default: ''
        },
        readonly: {
            type: Boolean,
            default: false
        },
        dataKey: {
            type: [String, Number, Function],
            default: ''
        },
        activeCell: {
            type: Object,
            default: null
        },
        cellValidation: {
            type: Object,
            required: true
        },
        isAppendSection: {
            type: Boolean,
            default: false
        },
        showSelectAll: {
            type: Boolean,
            default: false
        },
        frozenColumnAction: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            d_rows: this.rows,
            cell_prefix: `cell_${Utils.generateRecID()}`,
            d_activeCell: this.activeCell,
            d_cellValidation: this.cellValidation
        };
    },
    watch: {
        rows(newValue) {
            this.d_rows = newValue;
        },
        activeCell(newValue) {
            if (this.d_activeCell !== newValue) {
                this.d_activeCell = newValue;
            }
        },
        cellValidation(newValue) {
            this.d_cellValidation = newValue;
        }
    },
    computed: {},
    methods: {
        getComponentByFieldType(fieldType) {
            switch (fieldType) {
                case 'numeric':
                    return EditableNumericCell;
                case 'dropdown':
                    return EditableSelectCell;
                case 'lookup':
                    return EditableLookupCell;
                case 'date':
                    return EditableDateCell;
                case 'time':
                    return EditableTimeCell;
                case 'datetime':
                    return EditableDateTimeCell;
                case 'button':
                    return EditableButtonCell;

                case 'text':
                default:
                    return EditableInputCell;
            }
        },
        getFieldProperty(field, propName, defaultValue = null) {
            if (Object.prototype.hasOwnProperty.call(field, propName)) {
                return field[propName];
            }

            return defaultValue;
        },
        isFocusedCell(rowIndex, columnIndex) {
            return this.d_activeCell?.name === this.resolveCellName(rowIndex, columnIndex);
        },
        isEditableField(field) {
            return !this.readonly && (field.editable === undefined || field.editable);
        },
        isFocusableField(field) {
            return !this.readonly && (field.focusable === undefined || field.focusable);
        },
        /*actionVisible(action, item) {
            return this.$parentInstance.actionVisible(action, item);
        },
        actionDisabled(action, item) {
            if (typeof action.disabled === 'function') {
                try {
                    return action.disabled(item);
                } catch (e) {
                    return false;
                }
            }

            return !!action.disabled;
        },
        handleActionClick(action, item, index) {
            if (this.actionDisabled(action, item)) {
                return;
            }

            if (typeof action.handler === 'function') {
                if (this.canExecuteAction(index)) {
                    action.handler(item, index);
                } else {
                    this.showMessage(
                        this.$tc('Msg.Warning', 1),
                        this.$t('Msg.InvalidItemAction'),
                        'warning'
                    );
                }
            }
        },*/
        formatterHandle(formatter, item) {
            return value => {
                if (formatter) {
                    return formatter(value, item);
                }
                return value;
            };
        },
        updateCellValue(newValue, item, field) {
            if (this.isAppendSection) {
                let targetIndex = this.d_rows.indexOf(item);

                if (targetIndex !== -1) {
                    console.log(newValue, field);
                    if (field.dataType === 'lookup') {
                        this.$set(this.d_rows[targetIndex], field.key, newValue.code);

                        if (Object.hasOwn(field, 'props')) {
                            if (Object.hasOwn(field.props, 'valueDescription')) {
                                this.$set(
                                    this.d_rows[targetIndex],
                                    field.props.valueDescription,
                                    newValue.description
                                );
                            }

                            if (Object.hasOwn(field.props, 'valueAuxFields')) {
                                this.$set(
                                    this.d_rows[targetIndex],
                                    field.props.valueAuxFields,
                                    newValue.auxFields
                                );
                            }
                        }
                    } else {
                        this.$set(this.d_rows[targetIndex], field.key, newValue);
                    }
                }
            }
        },
        getRowKey(item, rowIndex) {
            return this.dataKey ? this.resolveFieldData(item, rowIndex) : `row_${rowIndex}`;
        },
        resolveFieldData(item, rowIndex) {
            if (typeof this.dataKey === 'function') {
                return this.dataKey(item, rowIndex);
            }

            return item[this.dataKey] ?? `row_${rowIndex}`;
        },
        resolveCellName(rowIndex, columnIndex) {
            return `${this.cell_prefix}-${rowIndex}-${columnIndex}`;
        },
        focusCell(rowIndex, columnIndex) {
            if (rowIndex < 0 || rowIndex >= this.d_rows.length) {
                return;
            }

            if (columnIndex < 0 || columnIndex >= this.columns.length) {
                return;
            }

            /*if (this.d_activeCell && this.d_cellValidation[this.d_activeCell.name]) {
                return;
            }*/

            const name = this.resolveCellName(rowIndex, columnIndex);
            const cell = this.$refs[name][0];

            if (cell) {
                this.updateCellActive(cell.createEventData(cell.value));
                cell.startEdit();
            }
        },
        setActiveCell($event) {
            /*const { name } = $event;

            if (this.d_activeCell && this.d_cellValidation[this.d_activeCell.name]) {
                const invalidCell = this.$refs[this.d_activeCell.name][0];

                if (invalidCell && name != this.d_activeCell.name) {
                    invalidCell.startEdit(false);
                }

                return;
            }*/

            this.updateCellActive($event);
        },
        handleRequestEdit($event) {
            const { name } = $event;
            /*if (this.d_activeCell.name && this.d_cellValidation[this.d_activeCell.name]) {
                return;
            }*/

            this.updateCellActive($event);
            const cell = this.$refs[name][0];

            if (cell) {
                cell.startEdit();
            }
        },
        updateValidation({ name, invalid }) {
            this.$set(this.d_cellValidation, name, invalid);
            this.$emit('update:cellValidation', this.d_cellValidation);
        },
        updateCellActive(cell) {
            this.d_activeCell = cell;
            this.$emit('update:activeCell', this.d_activeCell);
        },
        canExecuteAction(index) {
            const invalidCells = this.validateRow(index);

            if (invalidCells.length !== 0) {
                this.invalidateCells(invalidCells, index);
            }

            return invalidCells.length === 0;
        },
        onRowClick(rowIndex) {
            if (!this.isAppendSection) {
                const selectItem = this.d_rows[rowIndex];
                this.$parentInstance.rowClick({
                    rowKey: this.getRowKey(selectItem, rowIndex),
                    row: selectItem
                });
            }
        },
        onRowDblClick(rowIndex) {
            const selectItem = this.d_rows[rowIndex];
            this.$parentInstance.rowDblClick({
                rowKey: this.getRowKey(selectItem, rowIndex),
                row: selectItem
            });
        },
        isRowSelected(item, rowIndex) {
            return this.$parentInstance.isRowKeySelected(this.getRowKey(item, rowIndex));
        },

        onSelectRow(rowKey) {
            const item = this.d_rows.find((row, idx) => this.getRowKey(row, idx) === rowKey);
            this.$parentInstance.toggleRow(rowKey, item);
        },
        validateRow(rowIndex) {
            const invalidCells = [];
            this.columns.forEach((column, columnIndex) => {
                if (column.editable === false) return;
                const name = this.resolveCellName(rowIndex, columnIndex);
                const cell = this.$refs[name][0];

                if (cell) {
                    const valid = cell.validateCell();

                    if (!valid) {
                        invalidCells.push(column.key);
                    }
                }
            });

            return invalidCells;
        },
        invalidateCells(invalidateCells, rowIndex) {
            invalidateCells.forEach(columnKey => {
                const columnIndex = this.columns.findIndex(c => c.key === columnKey);

                if (columnIndex !== -1) {
                    const name = this.resolveCellName(rowIndex, columnIndex);
                    const cell = this.$refs[name][0];

                    if (cell) {
                        cell.invalid = true;
                    }
                }
            });
        }
    }
};
</script>
